import { useState, useCallback, useRef, useEffect } from 'react';
import type { DicePoolEntry, PoolRollResult } from '../../hooks/useDiceRoller';
import { parseNotation } from '../../hooks/useDiceRoller';
import { AnimatedDie } from './AnimatedDie';
import { DiceRollerOverlay, type DiceRollResultData, type DiceGroup } from './DiceRollerOverlay';
import { ModifierIcon } from './DiceIcons';
import { DICE_ICON_COMPONENTS } from './diceIconMap';
import { useDiceRollerStore } from '../../stores/diceRollerStore';
import {
  preRollDiceGroups,
  buildNotationFromPool,
  parseNotationToPoolState,
  type HistoryEntry,
} from './dice-roller/diceRollerUtils';
import type { RollMode } from '../../stores/diceRollerStore';

interface DiceRollerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onRollPool: (pool: DicePoolEntry[], modifier: number) => PoolRollResult;
}

const DICE_TYPES = [4, 6, 8, 10, 12, 20, 100] as const;

export function DiceRollerPanel({ isOpen, onClose, onRollPool }: DiceRollerPanelProps) {
  // Store access for programmatic rolls and shared roll mode
  const { pendingRoll, clearPendingRoll, rollMode, setRollMode, setLastRollResult } = useDiceRollerStore();

  // Pool state
  const [pool, setPool] = useState<DicePoolEntry[]>([]);
  const [modifier, setModifier] = useState(0);
  const [expandedDie, setExpandedDie] = useState<number | null>(null); // Track which die is expanded (by sides)

  // Roll state
  const [isRolling, setIsRolling] = useState(false);
  const [lastResult, setLastResult] = useState<PoolRollResult | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showResult, setShowResult] = useState(false);

  // 3D overlay state
  const [show3DOverlay, setShow3DOverlay] = useState(false);
  const [overlayDiceGroups, setOverlayDiceGroups] = useState<DiceGroup[]>([]);
  const [overlayModifier, setOverlayModifier] = useState(0);

  // Notation input
  const [notationInput, setNotationInput] = useState('');
  const [parsedNotation, setParsedNotation] = useState<ReturnType<typeof parseNotation> | null>(null);

  // Roll label for programmatic rolls
  const [rollLabel, setRollLabel] = useState<string | null>(null);

  // Modifier input modal
  const [showModifierInput, setShowModifierInput] = useState(false);
  const [tempModifier, setTempModifier] = useState(0);

  // Track which input method was used last for sync
  const syncSource = useRef<'notation' | 'pool'>('pool');
  const isUpdatingFromSync = useRef(false);

  // Parse notation as user types
  useEffect(() => {
    if (notationInput.trim()) {
      const parsed = parseNotation(notationInput);
      setParsedNotation(parsed);
    } else {
      setParsedNotation(null);
    }
  }, [notationInput]);

  // Sync: Notation → Pool (when user types in text field)
  useEffect(() => {
    if (syncSource.current !== 'notation' || isUpdatingFromSync.current) return;
    if (!parsedNotation?.valid) return;

    isUpdatingFromSync.current = true;
    const { pool: newPool, modifier: newMod, rollMode: detectedMode } = parseNotationToPoolState(parsedNotation);
    setPool(newPool);
    setModifier(newMod);
    if (detectedMode !== null) {
      setRollMode(detectedMode);
    } else if (newPool.length > 0) {
      // Reset to normal if no adv/dis detected and there's a pool
      setRollMode('normal');
    }
    isUpdatingFromSync.current = false;
  }, [parsedNotation, setRollMode]);

  // Sync: Pool → Notation (when user uses visual controls)
  useEffect(() => {
    if (syncSource.current !== 'pool' || isUpdatingFromSync.current) return;

    isUpdatingFromSync.current = true;
    const notation = buildNotationFromPool(pool, modifier, rollMode);
    setNotationInput(notation);
    isUpdatingFromSync.current = false;
  }, [pool, modifier, rollMode]);

  // Handle programmatic rolls from pendingRoll
  useEffect(() => {
    if (!pendingRoll || !isOpen) return;

    // Set the notation input to trigger roll
    syncSource.current = 'notation';
    setNotationInput(pendingRoll.notation);
    setRollLabel(pendingRoll.label);

    // Parse and auto-execute after a brief delay to allow state to update
    const parsed = parseNotation(pendingRoll.notation);
    if (parsed.valid) {
      // Use a short timeout to ensure state updates before executing
      const timeout = setTimeout(() => {
        // Trigger the roll programmatically
        const poolToRoll = parsed.pool;
        const mod = parsed.modifier;

        // Clear previous results
        setLastResult(null);
        setShowResult(false);

        // Pre-roll dice
        const diceGroups = preRollDiceGroups(poolToRoll);
        setOverlayDiceGroups(diceGroups);
        setOverlayModifier(mod);

        // Check for reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
          // Skip animation
          const result = onRollPool(poolToRoll, mod);
          setLastResult(result);
          setShowResult(true);
          setHistory((prev) => [
            { notation: pendingRoll.notation, total: result.total, timestamp: Date.now() },
            ...prev.slice(0, 9),
          ]);
          // Report nat 20 for crit auto-detect
          const wasNat20 = result.rolls.some(
            (g) => g.sides === 20 && g.results.some((v) => v === 20),
          );
          useDiceRollerStore.getState().setLastRollResult(pendingRoll.label, wasNat20);
        } else {
          // Show 3D overlay
          setIsRolling(true);
          setShow3DOverlay(true);
        }

        // Clear the pending roll
        clearPendingRoll();
      }, 100);

      return () => clearTimeout(timeout);
    } else {
      // Invalid notation, clear it
      clearPendingRoll();
    }
  }, [pendingRoll, isOpen, clearPendingRoll, onRollPool]);

  // Detect if any kept d20 value is a natural 20
  const detectNat20FromGroups = useCallback((groups: DiceGroup[], result: DiceRollResultData): boolean => {
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].sides !== 20) continue;
      // keptValues already accounts for advantage/disadvantage drop logic
      const startIndex = groups.slice(0, i).reduce((sum, prev) => sum + prev.count, 0);
      const groupValues = result.keptValues.slice(startIndex, startIndex + groups[i].count);
      // For d20 groups with keep, only kept values matter.
      // keptValues is the post-drop list, so any 20 here is a kept 20.
      if (groupValues.some((v) => v === 20)) return true;
    }
    return false;
  }, []);

  // Detect nat 20 from a PoolRollResult (reduced-motion path)
  const detectNat20FromPoolResult = useCallback((result: PoolRollResult): boolean => {
    for (const group of result.rolls) {
      if (group.sides !== 20) continue;
      if (group.results.some((v) => v === 20)) return true;
    }
    return false;
  }, []);

  // Add a die to the pool
  const addDie = useCallback((sides: number) => {
    syncSource.current = 'pool';
    setPool((prev) => {
      const existing = prev.find((d) => d.sides === sides);
      if (existing) {
        return prev.map((d) => (d.sides === sides ? { ...d, count: d.count + 1 } : d));
      }
      return [...prev, { sides, count: 1 }];
    });
  }, []);

  // Increment die count in pool
  const incrementDie = useCallback((sides: number) => {
    syncSource.current = 'pool';
    setPool((prev) =>
      prev.map((d) => (d.sides === sides ? { ...d, count: d.count + 1 } : d))
    );
  }, []);

  // Decrement die count in pool (removes when count reaches 0)
  const decrementDie = useCallback((sides: number) => {
    syncSource.current = 'pool';
    setPool((prev) => {
      const updated = prev.map((d) =>
        d.sides === sides ? { ...d, count: d.count - 1 } : d
      );
      return updated.filter((d) => d.count > 0);
    });
    // Close expanded pill if this die was removed
    setExpandedDie((current) => {
      const remaining = pool.find((d) => d.sides === sides);
      if (remaining && remaining.count <= 1) return null;
      return current;
    });
  }, [pool]);

  // Set keep mode for a die (All, Highest, Lowest)
  const setKeepMode = useCallback((sides: number, mode: 'all' | 'highest' | 'lowest') => {
    syncSource.current = 'pool';
    setPool((prev) =>
      prev.map((d) => {
        if (d.sides !== sides) return d;
        if (mode === 'all') {
          // Remove keep settings
          const { keep: _k, keepHighest: _kh, ...rest } = d;
          return rest;
        }
        // Default keep to count - 1 if not set
        return {
          ...d,
          keep: d.keep ?? Math.max(1, d.count - 1),
          keepHighest: mode === 'highest',
        };
      })
    );
  }, []);

  // Adjust keep count for a die
  const adjustKeepCount = useCallback((sides: number, delta: number) => {
    syncSource.current = 'pool';
    setPool((prev) =>
      prev.map((d) => {
        if (d.sides !== sides || d.keep === undefined) return d;
        const newKeep = Math.max(1, Math.min(d.count - 1, d.keep + delta));
        return { ...d, keep: newKeep };
      })
    );
  }, []);

  // Toggle expanded state for a die pill
  const toggleExpandedDie = useCallback((sides: number) => {
    setExpandedDie((current) => (current === sides ? null : sides));
  }, []);

  // Clear entire pool
  const clearPool = useCallback(() => {
    syncSource.current = 'pool';
    setPool([]);
    setModifier(0);
    setRollMode('normal');
    setExpandedDie(null);
    setLastResult(null);
    setNotationInput('');
    setParsedNotation(null);
    setRollLabel(null);
  }, [setRollMode]);

  // Handle roll mode change
  const handleRollModeChange = useCallback((mode: RollMode) => {
    syncSource.current = 'pool';
    setRollMode(mode);
  }, [setRollMode]);

  // Handle notation input change
  const handleNotationChange = useCallback((value: string) => {
    syncSource.current = 'notation';
    setNotationInput(value);
  }, []);

  // Apply advantage/disadvantage to pool for rolling
  const applyAdvantageMode = useCallback((poolToRoll: DicePoolEntry[], mode: RollMode): DicePoolEntry[] => {
    if (mode === 'normal') return poolToRoll;

    // For d20s, double them and set keep highest/lowest
    return poolToRoll.map((entry) => {
      if (entry.sides === 20 && entry.count === 1) {
        return {
          ...entry,
          count: 2,
          keep: 1,
          keepHighest: mode === 'advantage',
        };
      }
      return entry;
    });
  }, []);

  // Execute roll with 3D overlay
  const executeRoll = useCallback(() => {
    let poolToRoll: DicePoolEntry[];
    let mod: number;
    let notation: string;

    // Determine what to roll: parsed notation or visual pool
    if (parsedNotation?.valid && parsedNotation.pool.length > 0) {
      poolToRoll = parsedNotation.pool;
      mod = parsedNotation.modifier;
      notation = notationInput;
    } else if (pool.length > 0) {
      poolToRoll = applyAdvantageMode(pool, rollMode);
      mod = modifier;
      notation = buildNotationFromPool(pool, modifier, rollMode);
    } else {
      return; // Nothing to roll
    }

    // Clear previous results
    setLastResult(null);
    setShowResult(false);

    // Pre-roll dice to get predetermined values
    const diceGroups = preRollDiceGroups(poolToRoll);
    setOverlayDiceGroups(diceGroups);
    setOverlayModifier(mod);

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      // Skip 3D animation, compute result directly
      const result = onRollPool(poolToRoll, mod);
      setLastResult(result);
      setShowResult(true);
      setHistory((prev) => [
        { notation, total: result.total, timestamp: Date.now() },
        ...prev.slice(0, 9),
      ]);
      // Report nat 20 for crit auto-detect
      const wasNat20 = detectNat20FromPoolResult(result);
      setLastRollResult(rollLabel || '', wasNat20);
      return;
    }

    // Show 3D overlay with predetermined dice values
    setIsRolling(true);
    setShow3DOverlay(true);
  }, [pool, modifier, rollMode, parsedNotation, notationInput, onRollPool, applyAdvantageMode, rollLabel, detectNat20FromPoolResult, setLastRollResult]);

  // Handle 3D overlay close - receives result with predetermined values
  const handle3DOverlayClose = useCallback((diceBoxResult?: DiceRollResultData) => {
    setShow3DOverlay(false);
    setIsRolling(false);

    if (diceBoxResult) {
      // Convert result to PoolRollResult format, preserving dice grouping
      const poolResult: PoolRollResult = {
        pool: overlayDiceGroups.map(g => ({ sides: g.sides, count: g.count })),
        modifier: diceBoxResult.modifier,
        rolls: overlayDiceGroups.map((g, i) => {
          // Use actual DiceBox results, not pre-rolled animation values
          const startIndex = overlayDiceGroups.slice(0, i).reduce((sum, prev) => sum + prev.count, 0);
          return {
            sides: g.sides,
            results: diceBoxResult.diceValues.slice(startIndex, startIndex + g.count),
          };
        }),
        total: diceBoxResult.total,
        breakdown: `(${diceBoxResult.keptValues.join(' + ')})${diceBoxResult.droppedValues.length > 0 ? ` [dropped: ${diceBoxResult.droppedValues.join(', ')}]` : ''}${diceBoxResult.modifier !== 0 ? ` ${diceBoxResult.modifier > 0 ? '+' : ''}${diceBoxResult.modifier}` : ''} = ${diceBoxResult.total}`,
      };

      setLastResult(poolResult);
      setShowResult(true);

      // Report nat 20 detection for crit auto-detect
      const wasNat20 = detectNat20FromGroups(overlayDiceGroups, diceBoxResult);
      setLastRollResult(rollLabel || '', wasNat20);

      // Add to history
      const notationStr = overlayDiceGroups.map(g => `${g.count}d${g.sides}`).join('+') +
        (diceBoxResult.modifier !== 0 ? (diceBoxResult.modifier > 0 ? `+${diceBoxResult.modifier}` : `${diceBoxResult.modifier}`) : '');
      setHistory((prev) => [
        { notation: notationStr, total: diceBoxResult.total, timestamp: Date.now() },
        ...prev.slice(0, 9),
      ]);
    }
  }, [overlayDiceGroups, rollLabel, detectNat20FromGroups, setLastRollResult]);

  // Handle notation input key press
  const handleNotationKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && parsedNotation?.valid) {
      executeRoll();
    }
  }, [parsedNotation, executeRoll]);

  // Modifier modal handlers
  const openModifierModal = useCallback(() => {
    setTempModifier(modifier);
    setShowModifierInput(true);
  }, [modifier]);

  const acceptModifier = useCallback(() => {
    syncSource.current = 'pool';
    setModifier(tempModifier);
    setShowModifierInput(false);
  }, [tempModifier]);

  const cancelModifier = useCallback(() => {
    setShowModifierInput(false);
  }, []);

  // Check if we can roll
  const canRoll = (parsedNotation?.valid && parsedNotation.pool.length > 0) || pool.length > 0;

  // Check if there's anything to clear
  const hasContent = pool.length > 0 || modifier !== 0 || notationInput.trim() !== '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-background-card border border-accent-bronze/30 rounded-2xl animate-fade-in max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header with CLR button */}
        <div className="sticky top-0 z-10 bg-background-card flex items-center justify-between px-4 py-3 border-b border-background-elevated rounded-t-2xl">
          <h2 className="font-display text-lg text-accent-gold tracking-wider">DICE ROLLER</h2>
          <div className="flex items-center gap-2">
            {hasContent && (
              <button
                onClick={clearPool}
                className="px-3 py-1.5 text-xs font-display text-text-muted hover:text-error
                  bg-background-elevated hover:bg-error/10 rounded transition-colors min-h-[36px]"
              >
                CLR
              </button>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-elevated text-text-muted hover:text-text-primary transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Notation Input */}
        <div className="px-4 py-3 border-b border-background-elevated">
          <div className="relative">
            <input
              type="text"
              value={notationInput}
              onChange={(e) => handleNotationChange(e.target.value)}
              onKeyDown={handleNotationKeyDown}
              placeholder="2d20kh1+5, 4d6kh3, 8d6..."
              className="w-full px-4 py-3 bg-background-elevated border border-background-elevated
                rounded-lg font-mono text-text-primary placeholder:text-text-muted/50
                focus:outline-none focus:border-accent-gold/50 transition-colors"
            />
            {notationInput && (
              <button
                onClick={() => { handleNotationChange(''); setParsedNotation(null); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center
                  text-text-muted hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            )}
          </div>

          {/* Parse preview */}
          {parsedNotation && (
            <div className={`mt-2 text-sm ${parsedNotation.valid ? 'text-text-secondary' : 'text-error'}`}>
              {parsedNotation.valid ? parsedNotation.description : parsedNotation.error}
            </div>
          )}

          {/* Hint text when empty */}
          {!notationInput && !parsedNotation && (
            <p className="mt-2 text-xs text-text-muted text-center">
              ↓ Tap dice below or type notation above
            </p>
          )}
        </div>

        {/* Advantage/Disadvantage Toggle */}
        <div className="px-4 py-3 border-b border-background-elevated">
          <div className="flex rounded-lg overflow-hidden border border-background-elevated">
            {(['normal', 'advantage', 'disadvantage'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => handleRollModeChange(mode)}
                className={`flex-1 py-3 px-2 font-display text-sm tracking-wide transition-all
                  ${rollMode === mode
                    ? mode === 'advantage'
                      ? 'bg-success/20 text-success border-success'
                      : mode === 'disadvantage'
                        ? 'bg-error/20 text-error border-error'
                        : 'bg-accent-gold/20 text-accent-gold'
                    : 'bg-background-elevated text-text-muted hover:text-text-secondary'
                  }`}
              >
                {mode === 'normal' ? 'NORMAL' : mode === 'advantage' ? '▲ ADV' : '▼ DIS'}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-text-muted text-center">
            {rollMode === 'normal' && 'Roll dice normally'}
            {rollMode === 'advantage' && 'Roll 2d20 and keep the highest'}
            {rollMode === 'disadvantage' && 'Roll 2d20 and keep the lowest'}
          </p>
        </div>

        {/* Pool Display with expandable pills for keep settings */}
        <div className="px-4 py-3 border-b border-background-elevated min-h-[60px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-muted uppercase">Pool:</span>
            {pool.length === 0 ? (
              <span className="text-text-muted text-sm italic">Tap dice to add</span>
            ) : (
              <>
                {pool.map((entry) => {
                  const isExpanded = expandedDie === entry.sides;
                  const hasKeep = entry.keep !== undefined;
                  const canExpand = entry.count >= 2;
                  const keepMode = !hasKeep ? 'all' : entry.keepHighest ? 'highest' : 'lowest';
                  const keepNotation = hasKeep
                    ? (entry.keepHighest ? `kh${entry.keep}` : `kl${entry.keep}`)
                    : '';

                  return (
                    <div key={entry.sides} className="flex flex-col">
                      {/* Compact pill */}
                      <div
                        className={`flex items-center bg-accent-gold/20 border border-accent-gold/40 rounded-full overflow-hidden
                          ${canExpand ? 'cursor-pointer' : ''} ${isExpanded ? 'rounded-b-none border-b-0' : ''}`}
                      >
                        <button
                          onClick={() => decrementDie(entry.sides)}
                          className="w-8 h-8 flex items-center justify-center text-accent-gold
                            hover:bg-accent-gold/30 transition-colors active:scale-95"
                        >
                          −
                        </button>
                        <span
                          onClick={() => canExpand && toggleExpandedDie(entry.sides)}
                          className={`px-1 font-mono text-sm text-accent-gold ${canExpand ? 'hover:text-accent-gold-light' : ''}`}
                        >
                          {entry.count}d{entry.sides}{keepNotation}
                        </span>
                        <button
                          onClick={() => incrementDie(entry.sides)}
                          className="w-8 h-8 flex items-center justify-center text-accent-gold
                            hover:bg-accent-gold/30 transition-colors active:scale-95"
                        >
                          +
                        </button>
                        {canExpand && (
                          <button
                            onClick={() => toggleExpandedDie(entry.sides)}
                            className={`w-6 h-8 flex items-center justify-center text-text-muted
                              hover:text-accent-gold transition-colors text-xs ${isExpanded ? 'rotate-180' : ''}`}
                          >
                            ▼
                          </button>
                        )}
                      </div>

                      {/* Expanded keep settings card */}
                      {isExpanded && (
                        <div className="bg-background-elevated border border-accent-gold/40 border-t-0 rounded-b-lg p-3 animate-fade-in">
                          {/* Keep mode selector */}
                          <div className="flex gap-1 mb-2">
                            {(['all', 'highest', 'lowest'] as const).map((mode) => (
                              <button
                                key={mode}
                                onClick={() => setKeepMode(entry.sides, mode)}
                                className={`flex-1 py-1.5 px-2 text-xs font-display uppercase rounded transition-colors
                                  ${keepMode === mode
                                    ? 'bg-accent-gold/30 text-accent-gold border border-accent-gold/50'
                                    : 'bg-background-card text-text-muted hover:text-text-secondary border border-transparent'
                                  }`}
                              >
                                {mode === 'all' ? 'All' : mode === 'highest' ? 'High' : 'Low'}
                              </button>
                            ))}
                          </div>

                          {/* Keep count stepper (only shown when keep mode is highest or lowest) */}
                          {hasKeep && (
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-text-muted">Keep:</span>
                              <button
                                onClick={() => adjustKeepCount(entry.sides, -1)}
                                disabled={entry.keep === 1}
                                className="w-8 h-8 flex items-center justify-center bg-background-card
                                  hover:bg-accent-gold/20 disabled:opacity-30 disabled:cursor-not-allowed
                                  rounded text-accent-gold transition-colors"
                              >
                                −
                              </button>
                              <span className="w-8 text-center font-mono text-accent-gold font-bold">
                                {entry.keep}
                              </span>
                              <button
                                onClick={() => adjustKeepCount(entry.sides, 1)}
                                disabled={entry.keep === entry.count - 1}
                                className="w-8 h-8 flex items-center justify-center bg-background-card
                                  hover:bg-accent-gold/20 disabled:opacity-30 disabled:cursor-not-allowed
                                  rounded text-accent-gold transition-colors"
                              >
                                +
                              </button>
                            </div>
                          )}

                          {/* Done button */}
                          <button
                            onClick={() => setExpandedDie(null)}
                            className="w-full mt-2 py-1.5 text-xs font-display uppercase bg-accent-gold/20
                              hover:bg-accent-gold/30 text-accent-gold rounded transition-colors"
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {modifier !== 0 && (
                  <span className="px-3 py-1.5 bg-background-elevated rounded-full text-sm font-mono text-text-secondary min-h-[36px] flex items-center">
                    {modifier > 0 ? '+' : ''}{modifier}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Dice Buttons Grid */}
        <div className="px-4 py-4">
          <div className="grid grid-cols-4 gap-2">
            {DICE_TYPES.map((sides) => {
              const IconComponent = DICE_ICON_COMPONENTS[sides];
              return (
                <button
                  key={sides}
                  onClick={() => addDie(sides)}
                  className="aspect-square flex flex-col items-center justify-center gap-1 min-h-[64px]
                    bg-background-elevated hover:bg-background-elevated/80 border border-background-elevated
                    hover:border-accent-gold/50 rounded-lg transition-all active:scale-95
                    group"
                >
                  <IconComponent className="w-7 h-7 text-text-secondary group-hover:text-accent-gold transition-colors" />
                  <span className="text-xs font-mono text-text-muted group-hover:text-accent-gold/80 transition-colors">
                    d{sides === 100 ? '%' : sides}
                  </span>
                </button>
              );
            })}
            {/* Modifier button */}
            <button
              onClick={openModifierModal}
              className="aspect-square flex flex-col items-center justify-center gap-1 min-h-[64px]
                bg-background-elevated hover:bg-background-elevated/80 border border-background-elevated
                hover:border-accent-gold/50 rounded-lg transition-all active:scale-95
                group"
            >
              <ModifierIcon className="w-7 h-7 text-text-secondary group-hover:text-accent-gold transition-colors" />
              <span className="text-xs font-mono text-text-muted group-hover:text-accent-gold/80 transition-colors">mod</span>
            </button>
          </div>
        </div>

        {/* Roll Button */}
        <div className="px-4 pb-4">
          <button
            onClick={executeRoll}
            disabled={!canRoll || isRolling}
            className="w-full py-4 bg-accent-gold hover:bg-accent-gold-light disabled:bg-background-elevated
              disabled:text-text-muted text-background-primary font-display text-lg tracking-wider
              rounded-lg transition-all active:scale-[0.98] disabled:cursor-not-allowed min-h-[56px]"
          >
            {isRolling ? 'ROLLING...' : 'ROLL'}
          </button>
        </div>

        {/* Result Display - Prominent styling */}
        {(isRolling || showResult) && (
          <div className="px-4 pb-4">
            <div className="relative bg-gradient-to-b from-background-elevated to-background-card
              rounded-xl p-6 text-center border border-accent-bronze/30
              shadow-lg shadow-accent-gold/10 overflow-hidden">
              {/* Subtle glow background effect */}
              <div className="absolute inset-0 bg-gradient-radial from-accent-gold/5 to-transparent pointer-events-none" />

              <div className="relative z-10">
                {/* Show roll label if present, otherwise "Result" */}
                <div className="text-xs text-accent-gold/70 uppercase tracking-wider font-display mb-3">
                  {rollLabel || 'Result'}
                </div>

                {/* Final result display */}
                {showResult && lastResult && (
                  <>
                    {/* Large prominent total - hero display */}
                    <div className="animate-result-pop mb-4">
                      <span
                        className="text-6xl md:text-7xl font-display text-accent-gold"
                        style={{
                          textShadow: '0 0 20px rgba(255, 85, 0, 0.4), 0 0 40px rgba(255, 85, 0, 0.2)',
                        }}
                      >
                        {lastResult.total}
                      </span>
                    </div>

                    {/* Individual dice results - smaller below */}
                    <div className="flex justify-center gap-2 flex-wrap mb-3">
                      {lastResult.rolls.map((diceGroup, gi) =>
                        diceGroup.results.map((result, ri) => (
                          <AnimatedDie
                            key={`${gi}-${ri}`}
                            sides={diceGroup.sides}
                            result={result}
                            phase="result"
                            delay={0}
                          />
                        ))
                      )}
                      {lastResult.modifier !== 0 && (
                        <span className="w-12 h-12 flex items-center justify-center
                          bg-background-card border border-accent-bronze/30 rounded-lg
                          font-mono text-text-secondary font-bold text-sm"
                        >
                          {lastResult.modifier > 0 ? '+' : ''}{lastResult.modifier}
                        </span>
                      )}
                    </div>

                    {/* Breakdown */}
                    <div className="text-sm text-text-muted font-mono">
                      {lastResult.breakdown}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="px-4 pb-4">
            <div className="text-xs text-text-muted uppercase mb-2">History</div>
            <div className="space-y-1 max-h-[100px] overflow-y-auto">
              {history.map((entry, i) => (
                <div
                  key={entry.timestamp}
                  className="flex justify-between text-sm text-text-secondary"
                  style={{ opacity: 1 - i * 0.1 }}
                >
                  <span className="font-mono">{entry.notation}</span>
                  <span className="font-mono font-bold">{entry.total}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Safe area padding for mobile */}
        <div className="h-4" />
      </div>

      {/* Modifier Input Modal */}
      {showModifierInput && (
        <div className="fixed inset-0 flex items-center justify-center z-[60]">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={cancelModifier}
          />
          <div className="relative bg-background-card border border-accent-bronze/30 rounded-xl p-6 shadow-xl min-w-[280px]">
            <div className="text-center mb-4">
              <div className="text-sm text-text-muted uppercase tracking-wider mb-1">Modifier</div>
              <div className="text-4xl font-mono font-bold text-accent-gold">
                {tempModifier >= 0 ? '+' : ''}{tempModifier}
              </div>
            </div>

            {/* Stepper buttons */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={() => setTempModifier(prev => prev - 1)}
                className="w-16 h-16 flex items-center justify-center bg-background-elevated
                  hover:bg-error/20 border border-background-elevated hover:border-error/50
                  rounded-xl text-3xl text-text-secondary hover:text-error transition-all active:scale-95"
              >
                −
              </button>
              <button
                onClick={() => setTempModifier(0)}
                className="w-12 h-12 flex items-center justify-center bg-background-elevated
                  hover:bg-background-elevated/80 border border-background-elevated
                  rounded-lg text-sm font-display text-text-muted hover:text-text-primary transition-all"
              >
                0
              </button>
              <button
                onClick={() => setTempModifier(prev => prev + 1)}
                className="w-16 h-16 flex items-center justify-center bg-background-elevated
                  hover:bg-success/20 border border-background-elevated hover:border-success/50
                  rounded-xl text-3xl text-text-secondary hover:text-success transition-all active:scale-95"
              >
                +
              </button>
            </div>

            {/* Quick values */}
            <div className="flex justify-center gap-2 mb-6">
              {[-5, -2, +2, +5].map((val) => (
                <button
                  key={val}
                  onClick={() => setTempModifier(val)}
                  className={`px-3 py-2 rounded-lg text-sm font-mono transition-all
                    ${tempModifier === val
                      ? 'bg-accent-gold/20 text-accent-gold border border-accent-gold/50'
                      : 'bg-background-elevated text-text-muted hover:text-text-primary border border-transparent'
                    }`}
                >
                  {val >= 0 ? '+' : ''}{val}
                </button>
              ))}
            </div>

            {/* Accept/Cancel buttons */}
            <div className="flex gap-3">
              <button
                onClick={cancelModifier}
                className="flex-1 py-3 bg-background-elevated hover:bg-background-elevated/80
                  text-text-secondary hover:text-text-primary font-display tracking-wider
                  rounded-lg transition-all min-h-[48px]"
              >
                CANCEL
              </button>
              <button
                onClick={acceptModifier}
                className="flex-1 py-3 bg-accent-gold hover:bg-accent-gold-light
                  text-background-primary font-display tracking-wider
                  rounded-lg transition-all min-h-[48px]"
              >
                ACCEPT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3D Dice Roller Overlay */}
      <DiceRollerOverlay
        isOpen={show3DOverlay}
        onClose={handle3DOverlayClose}
        diceGroups={overlayDiceGroups}
        modifier={overlayModifier}
      />
    </div>
  );
}
